import { useEffect, useRef, useCallback } from 'react';

import type { Rive } from '@rive-app/react-canvas';
import { useTranslation } from 'react-i18next';

import { RIVE_BANNER_JACKPOT_KEY } from '../../components/banners/headers/rive/constants';
import { findCrownSlotsProviderId } from '../../components/banners/headers/rive/utils';
import { formatJackpotNumber } from '../../components/JackpotBalance/helpers';
import { useStores } from '../../stores/useStores';
import { formatValueWithKFloor } from '../../utils/helpers';

const TICK_MS = 60;

export const useRiveTextUpdater = ({ rive, isRivePlaying }: { rive?: Rive | null; isRivePlaying: boolean }) => {
  const { rewardsStore, categoriesStore, coinTypeStore } = useStores();
  const { t } = useTranslation();
  const isValidInputRef = useRef<boolean | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastTickTsRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const riveRef = useRef<Rive | null | undefined>(rive);

  useEffect(() => {
    riveRef.current = rive;
  }, [rive]);

  const getFormattedJackpot = useCallback(() => {
    const crownSlotsProviderId = findCrownSlotsProviderId(categoriesStore);
    if (!crownSlotsProviderId) return '';

    const jackpotAmount = rewardsStore.jackpot.get(crownSlotsProviderId);
    const fastJackpotAmount = rewardsStore.fastJackpot.get(crownSlotsProviderId);
    const totalJackpot = Number(jackpotAmount ?? 0) + Number(fastJackpotAmount ?? 0);

    if (totalJackpot === 0) return '';
    const isGC = coinTypeStore.coinType === 'GC';
    return isGC ? formatValueWithKFloor(totalJackpot, t) : formatJackpotNumber(totalJackpot, 0);
  }, [categoriesStore, rewardsStore.jackpot, rewardsStore.fastJackpot, coinTypeStore.coinType, t]);

  useEffect(() => {
    isMountedRef.current = true;
    lastTickTsRef.current = 0;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    const tick = (ts: number) => {
      if (!isMountedRef.current) {
        return;
      }

      const currentRive = riveRef.current;
      if (!currentRive) {
        if (isMountedRef.current && isRivePlaying) {
          rafIdRef.current = requestAnimationFrame(tick);
        }
        return;
      }

      try {
        if (!currentRive.isPlaying && !isRivePlaying) {
          return;
        }

        if (isValidInputRef.current === false || !isRivePlaying) {
          return;
        }

        if (lastTickTsRef.current === 0) {
          lastTickTsRef.current = ts;
        }
        const sinceLast = ts - lastTickTsRef.current;

        if (sinceLast >= TICK_MS && isValidInputRef.current === true) {
          const value = getFormattedJackpot();
          try {
            currentRive.setTextRunValue(RIVE_BANNER_JACKPOT_KEY, value);
            lastTickTsRef.current = ts;
          } catch (error) {
            console.warn('Rive instance was cleaned up, stopping text updates');
            return;
          }
        }

        if (isMountedRef.current) {
          rafIdRef.current = requestAnimationFrame(tick);
        }
      } catch (error) {
        console.warn('Rive instance was cleaned up, stopping text updates');
        return;
      }
    };

    if (isMountedRef.current) {
      rafIdRef.current = requestAnimationFrame(tick);
    }

    return () => {
      isMountedRef.current = false;
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [rive, getFormattedJackpot, isRivePlaying]);

  useEffect(() => {
    if (!rive || !isRivePlaying) {
      return;
    }

    try {
      if (!rive.isPlaying) {
        return;
      }

      if (isValidInputRef.current === false) {
        return;
      }

      const currentValue = rive.getTextRunValue(RIVE_BANNER_JACKPOT_KEY);

      if (currentValue === undefined) {
        isValidInputRef.current = false;
        return;
      }

      isValidInputRef.current = true;
    } catch (error) {
      isValidInputRef.current = false;
    }
  }, [rive, isRivePlaying]);
};
